import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

function MonthGrid({ year, month, campaigns, geofences }) {
  const first = new Date(Date.UTC(year, month, 1));
  const daysInMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  const startOffset = (first.getUTCDay() + 6) % 7; // Monday start
  const cells = [...Array(startOffset).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];

  const dayIso = (d) => `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

  return (
    <div className="card-surface p-5" data-testid={`calendar-${year}-${month + 1}`}>
      <h3 className="font-display font-semibold text-white mb-4">{MONTHS[month]} {year}</h3>
      <div className="grid grid-cols-7 gap-1 text-center text-[10px] tracking-widest text-slate-500 uppercase mb-2">
        {["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"].map((d) => <span key={d}>{d}</span>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          if (!d) return <span key={`x${i}`} />;
          const iso = dayIso(d);
          const campaignActive = campaigns.some((c) => c.start_date <= iso && c.end_date >= iso);
          const isStart = campaigns.some((c) => c.start_date === iso);
          const isEnd = campaigns.some((c) => c.end_date === iso);
          const activations = geofences.filter((g) => g.start_date === iso || g.end_date === iso);
          const oneDay = geofences.some((g) => g.start_date === iso && g.end_date === iso);
          return (
            <div key={iso}
              className={`aspect-square rounded flex flex-col items-center justify-center text-xs relative transition-colors
                ${campaignActive ? "bg-blue-600/15 text-blue-200" : "text-slate-500"}
                ${isStart || isEnd ? "ring-1 ring-cyan-400 font-bold text-cyan-300" : ""}
                ${oneDay ? "bg-cyan-500/20 ring-1 ring-cyan-300 text-cyan-200 font-bold" : ""}`}
              title={activations.map((a) => a.location_name).join(", ")}
              data-testid={oneDay ? "calendar-one-day-activation" : isStart ? "calendar-campaign-start" : isEnd ? "calendar-campaign-end" : undefined}>
              {d}
              {activations.length > 0 && <span className="w-1 h-1 rounded-full bg-cyan-300 mt-0.5" />}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function CalendarPage() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/client/calendar").then((r) => setData(r.data)).catch(() => setData({ campaigns: [], geofences: [] }));
  }, []);

  if (!data) return <Skeleton className="h-64 bg-white/5" />;

  const months = new Set();
  data.campaigns.forEach((c) => {
    [c.start_date, c.end_date].forEach((d) => { if (d) months.add(d.slice(0, 7)); });
  });
  data.geofences.forEach((g) => {
    [g.start_date, g.end_date].forEach((d) => { if (d) months.add(d.slice(0, 7)); });
  });
  const monthList = [...months].sort();

  return (
    <div className="space-y-6" data-testid="calendar-page">
      <div>
        <p className="section-label">Scheduling</p>
        <h2 className="font-display text-2xl sm:text-3xl font-bold text-white tracking-tight mt-1">Campaign Calendar</h2>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        {monthList.map((ym) => {
          const [y, m] = ym.split("-").map(Number);
          return <MonthGrid key={ym} year={y} month={m - 1} campaigns={data.campaigns} geofences={data.geofences} />;
        })}
        {monthList.length === 0 && <p className="text-slate-500">No scheduled campaigns.</p>}
      </div>

      <div className="card-surface p-5">
        <h3 className="font-display font-semibold text-sm tracking-wide text-white mb-4">SCHEDULED ACTIVATIONS</h3>
        <div className="space-y-3">
          {data.geofences.map((g) => (
            <div key={g.id} className="flex flex-wrap items-center justify-between gap-3 border-b border-white/5 last:border-0 pb-3 last:pb-0" data-testid={`activation-${g.id}`}>
              <div>
                <p className="text-sm font-semibold text-white">{g.location_name}</p>
                <p className="text-xs text-slate-500">{g.city} · {g.radius_meters}m geofence</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-cyan-300 font-semibold">
                  {g.start_date === g.end_date ? `${g.start_date} (one-day activation)` : `${g.start_date} → ${g.end_date}`}
                </p>
                <p className="text-[10px] tracking-widest text-slate-500 uppercase">{g.status}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {data.campaigns[0] && (
        <Link to={`/portal/campaigns/${data.campaigns[0].id}`} className="text-sm text-cyan-400 hover:text-cyan-300" data-testid="calendar-open-campaign">
          Open campaign details →
        </Link>
      )}
    </div>
  );
}
